import {
	Box,
	ErrorFeedback,
	Grid,
	Heading,
	Loader
} from "@itandi/itandi-bb-ui";
import { useRouter } from "next/router";
import React from "react";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { DealSearch } from "~/components/specifics/deals/index/search/DealSearch";
import { DealTable } from "~/components/specifics/deals/index/table/DealTable";
import { useCompanyVendors } from "~/hooks/useCompanyVendors";
import { useDeals } from "~/hooks/useDeals";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import { useStatuses } from "~/hooks/useStatuses";
import type { DealSearchCondition } from "~/models/Deal";
import type { ItandiAccount } from "~/models/ItandiAccount";
import { PATH } from "~/pages/deals";
import {
	buildSearchConditionByParams,
	convertQueryStringUrl
} from "~/utils/DealSearchConditionUtils";

type Props = {
	session: ItandiAccount;
};

export const Page: React.FC<Props> = ({ session }: Props) => {
	const router = useRouter();
	const { match: isSp, isLoaded: windowLoaded } = useMediaQuery(MEDIA_QUERY.sp);
	const isMasterAccount = session.store?.masterAccount ?? false;
	const [dealSearchCondition, setDealSearchCondition] =
		React.useState<DealSearchCondition>(
			buildSearchConditionByParams(router.query, !isMasterAccount)
		);
	// isLoading中用の総ページ数State
	const [totalPage, setTotalPage] = React.useState<number>(1);
	const { data: statuses, isLoading: isStatusLoading } = useStatuses().index();
	const {
		data: paginateDeal,
		mutate: mutatePaginateDeal,
		isLoading: isDealLoading,
		error: paginateDealError
	} = useDeals().index(dealSearchCondition);
	const { data: companyVendors, isLoading: isCompanyVendorsLoading } =
		useCompanyVendors().index();
	const handleChangeCondition = React.useCallback(
		async (newCondition: DealSearchCondition): Promise<void> => {
			setDealSearchCondition(newCondition);
			const newUrl = convertQueryStringUrl(PATH, newCondition);
			await router.replace(newUrl);
			setTotalPage(paginateDeal?.sumPageCount || totalPage);
			await mutatePaginateDeal?.().then();
		},
		[mutatePaginateDeal, paginateDeal, totalPage, router]
	);
	if (!windowLoaded) {
		return null;
	}
	const statusNames =
		statuses === null
			? {}
			: Object.fromEntries(statuses.map((status) => [status.id, status.name]));
	return (
		<DefaultV2>
			<Loader isLoading={isDealLoading || isStatusLoading} position="screen" />
			<Box marginTop={8} marginX={16}>
				<Grid gap={8}>
					<Heading as="h1">案件一覧</Heading>
					{companyVendors !== null && !isCompanyVendorsLoading && (
						<DealSearch
							companyVendors={companyVendors}
							dealSearchCondition={dealSearchCondition}
							handleChangeCondition={handleChangeCondition}
							isSp={isSp}
						/>
					)}
					{paginateDeal !== null && (
						<DealTable
							dealSearchCondition={dealSearchCondition}
							deals={paginateDeal.objects}
							handleChangeCondition={handleChangeCondition}
							isSp={isSp}
							statusNames={statusNames}
							total={paginateDeal.count}
						/>
					)}
					{paginateDealError?.response ? (
						<ErrorFeedback>
							{paginateDealError.response.data.message}
						</ErrorFeedback>
					) : null}
				</Grid>
			</Box>
		</DefaultV2>
	);
};
